/**
 * CustomFieldsSection
 *
 * Renders enabled custom fields for a given object type as inline-editable inputs.
 * Custom fields are defined in the Attributes settings (isCustomField: true) and
 * values are stored in the object's metadata JSONB column keyed by attribute.key.
 *
 * Supports:
 *  - All field types: text, number, date, boolean, select, multiselect
 *  - All display types: cards, pills, dropdown, radio, toggle, text-input, number-input, date-picker
 *  - Parent-child hierarchies: child fields are hidden until their parent has a value,
 *    and only show the options mapped to the current parent value.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Settings2 } from 'lucide-react';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { metadataAfterChange, placementOf } from '../../../lib/customFieldPlacement';
import type { Attribute, CustomFieldDisplayType, CustomFieldPlacementTab } from '../../../types/prioritization';

// ============================================================================
// Types
// ============================================================================

interface CustomFieldsSectionProps {
  itemId: string;
  objectType?: string;
  metadata: Record<string, unknown> | undefined;
  readOnly?: boolean;
  onSave: (updates: { metadata: Record<string, unknown> }) => void;
  /**
   * Item page: only the fields placed on this tab (Settings → Custom Fields →
   * Shows on). Omitted: every enabled field, as before.
   */
  tab?: CustomFieldPlacementTab;
}

// ============================================================================
// Helpers
// ============================================================================

function effectiveDisplayType(field: Attribute): CustomFieldDisplayType {
  if (field.customDisplayType) return field.customDisplayType;
  switch (field.type) {
    case 'boolean':     return 'toggle';
    case 'select':      return field.options && field.options.length <= 5 ? 'pills' : 'dropdown';
    case 'multiselect': return 'pills';
    case 'number':      return 'number-input';
    case 'date':        return 'date-picker';
    default:            return 'text-input';
  }
}

/** Returns the options this field should show given the current metadata state. */
function resolveOptions(field: Attribute, meta: Record<string, unknown>): string[] {
  if (field.parentKey && field.conditionalOptions) {
    const parentVal = meta[field.parentKey] as string | undefined;
    if (!parentVal) return [];
    return field.conditionalOptions[parentVal] ?? [];
  }
  return field.options ?? [];
}

// ============================================================================
// Cards display type — altitude-style tappable cards
// ============================================================================

function CardsInput({
  options,
  value,
  multi,
  readOnly,
  onChange,
}: {
  options: string[];
  value: unknown;
  multi: boolean;
  readOnly: boolean;
  onChange: (val: unknown) => void;
}) {
  const selected: string[] = multi
    ? Array.isArray(value) ? (value as string[]) : []
    : value != null && value !== '' ? [String(value)] : [];

  const toggle = (opt: string) => {
    if (multi) {
      const next = selected.includes(opt)
        ? selected.filter((v) => v !== opt)
        : [...selected, opt];
      onChange(next.length === 0 ? null : next);
    } else {
      onChange(selected[0] === opt ? null : opt);
    }
  };

  if (readOnly) {
    if (selected.length === 0) return <span className="text-sm text-[var(--text-ter)] italic">—</span>;
    return (
      <div className="flex flex-wrap gap-2">
        {selected.map((s) => (
          <span key={s} className="text-xs px-3 py-1.5 border-2 border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] font-medium">
            {s}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-4 py-2  border-2 text-sm font-medium transition-all ${
              active
                ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] shadow-sm'
                : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/50'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Pills display type — compact chip buttons
// ============================================================================

function PillsInput({
  options,
  value,
  multi,
  readOnly,
  onChange,
}: {
  options: string[];
  value: unknown;
  multi: boolean;
  readOnly: boolean;
  onChange: (val: unknown) => void;
}) {
  const selected: string[] = multi
    ? Array.isArray(value) ? (value as string[]) : []
    : value != null && value !== '' ? [String(value)] : [];

  const toggle = (opt: string) => {
    if (multi) {
      const next = selected.includes(opt)
        ? selected.filter((v) => v !== opt)
        : [...selected, opt];
      onChange(next.length === 0 ? null : next);
    } else {
      onChange(selected[0] === opt ? null : opt);
    }
  };

  if (readOnly) {
    if (selected.length === 0) return <span className="text-sm text-[var(--text-ter)] italic">—</span>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {selected.map((s) => (
          <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 border border-primary-300 font-medium">
            {s}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              active
                ? 'bg-primary-100 text-primary-700 border-primary-300'
                : 'bg-[var(--surface)] text-[var(--text-sec)] border-[var(--rule)] hover:border-[var(--rule)]'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Radio display type
// ============================================================================

function RadioInput({
  options,
  value,
  readOnly,
  onChange,
}: {
  options: string[];
  value: unknown;
  readOnly: boolean;
  onChange: (val: unknown) => void;
}) {
  const selected = value != null && value !== '' ? String(value) : '';

  if (readOnly) {
    if (!selected) return <span className="text-sm text-[var(--text-ter)] italic">—</span>;
    return <span className="text-sm text-[var(--text-pri)] font-medium">{selected}</span>;
  }

  return (
    <div className="flex flex-wrap gap-4">
      {options.map((opt) => (
        <label key={opt} className="flex items-center gap-2 cursor-pointer group">
          <input
            type="radio"
            name={`radio-${opt}`}
            checked={selected === opt}
            onChange={() => onChange(opt === selected ? null : opt)}
            className="w-4 h-4 text-[var(--text-ter)] border-[var(--rule)] focus:ring-indigo-500"
          />
          <span className="text-sm text-[var(--text-sec)] group-hover:text-[var(--text-pri)]">{opt}</span>
        </label>
      ))}
    </div>
  );
}

/**
 * One custom field's editor, alone — for the item page's "At a glance" column
 * and the quick panel. Saves on change, as the section does.
 */
export function CustomFieldInput({
  field,
  metadata,
  readOnly = false,
  onSave,
}: {
  field: Attribute;
  metadata: Record<string, unknown> | undefined;
  readOnly?: boolean;
  onSave: (updates: { metadata: Record<string, unknown> }) => void;
}) {
  const attributes = useAttributesStore((s) => s.attributes);
  const meta = metadata ?? {};
  const children = attributes.filter((a) => a.isCustomField === true && a.enabled !== false && a.parentKey === field.key);
  return (
    <FieldInput
      field={field}
      value={meta[field.key]}
      meta={meta}
      readOnly={readOnly}
      onChange={(val) => onSave({ metadata: metadataAfterChange(meta, field.key, val, children) })}
    />
  );
}

// ============================================================================
// Main FieldInput dispatcher
// ============================================================================

function FieldInput({
  field,
  value,
  meta,
  readOnly,
  onChange,
}: {
  field: Attribute;
  value: unknown;
  meta: Record<string, unknown>;
  readOnly: boolean;
  onChange: (val: unknown) => void;
}) {
  const [localValue, setLocalValue] = useState<string>(
    value !== null && value !== undefined ? String(value) : ''
  );
  // Follow the saved value when it changes elsewhere (the same field can show
  // on a tab and in "At a glance" at once).
  const saved = value !== null && value !== undefined ? String(value) : '';
  useEffect(() => {
    setLocalValue(saved);
  }, [saved]);

  const displayType = effectiveDisplayType(field);
  const options = resolveOptions(field, meta);

  // Unified read-only for simple scalar values
  if (readOnly) {
    if (value === null || value === undefined || value === '') {
      return <span className="text-sm text-[var(--text-ter)] italic">—</span>;
    }
    if (field.type === 'boolean') {
      return (
        <span className={`inline-flex items-center gap-1 text-sm font-medium ${value ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }
    // cards / pills / radio — delegate to their components
    if (displayType === 'cards') {
      return <CardsInput options={options} value={value} multi={field.type === 'multiselect'} readOnly onChange={onChange} />;
    }
    if (displayType === 'pills') {
      return <PillsInput options={options} value={value} multi={field.type === 'multiselect'} readOnly onChange={onChange} />;
    }
    if (displayType === 'radio') {
      return <RadioInput options={options} value={value} readOnly onChange={onChange} />;
    }
    return <span className="text-sm text-[var(--text-pri)]">{String(value)}</span>;
  }

  // === Edit mode ===

  if (displayType === 'toggle' || field.type === 'boolean') {
    return (
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${
          value ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'
        }`}
      >
        <span className={`block w-3.5 h-3.5 bg-[var(--bg-raised)] rounded-full shadow transition-transform mx-0.5 my-0.5 ${value ? 'translate-x-4' : 'translate-x-0'}`} />
      </button>
    );
  }

  if (displayType === 'cards' && options.length > 0) {
    return <CardsInput options={options} value={value} multi={field.type === 'multiselect'} readOnly={false} onChange={onChange} />;
  }

  if (displayType === 'pills' && options.length > 0) {
    return <PillsInput options={options} value={value} multi={field.type === 'multiselect'} readOnly={false} onChange={onChange} />;
  }

  if (displayType === 'radio' && options.length > 0) {
    return <RadioInput options={options} value={value} readOnly={false} onChange={onChange} />;
  }

  if (displayType === 'dropdown' && options.length > 0) {
    if (field.type === 'multiselect') {
      // Multi-select via pills fallback when dropdown is chosen
      return <PillsInput options={options} value={value} multi readOnly={false} onChange={onChange} />;
    }
    return (
      <select
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className="w-full text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-[var(--text-pri)]"
      >
        <option value="">— Select —</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  if (displayType === 'date-picker') {
    return (
      <input
        type="date"
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className="text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-[var(--text-pri)]"
      />
    );
  }

  // text-input / number-input / fallback
  return (
    <input
      type={displayType === 'number-input' ? 'number' : 'text'}
      value={localValue}
      placeholder={field.description ?? `Enter ${field.label.toLowerCase()}…`}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => {
        if (displayType === 'number-input') {
          const n = parseFloat(localValue);
          onChange(isNaN(n) ? null : n);
        } else {
          onChange(localValue === '' ? null : localValue);
        }
      }}
      className="w-full text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-[var(--text-pri)] placeholder-gray-400"
    />
  );
}

// ============================================================================
// Main component
// ============================================================================

export function CustomFieldsSection({ metadata, objectType = 'item', readOnly = false, onSave, tab }: CustomFieldsSectionProps) {
  const attributes = useAttributesStore((s) => s.attributes);
  // "Manage" opens the custom fields settings — drawn only for people who may change them.
  const mayManage = useUserStore((s) => s.can('attributes.define') || s.can('admin.settings'));

  // All enabled custom fields for this object type
  const allFields = useMemo(
    () => attributes.filter(
      (a) => a.isCustomField === true && a.enabled !== false && (a.objectType ?? 'item') === objectType
    ),
    [attributes, objectType]
  );

  // Only render top-level fields (no parentKey). Child fields are rendered inline
  // below their parent field once the parent has a value.
  const topLevelFields = useMemo(
    () => allFields.filter((f) => !f.parentKey && (tab === undefined || placementOf(f).tab === tab)),
    [allFields, tab]
  );

  // Map child fields by their parentKey for quick lookup
  const childFieldsByParent = useMemo(() => {
    const map = new Map<string, Attribute[]>();
    for (const f of allFields) {
      if (f.parentKey) {
        const list = map.get(f.parentKey) ?? [];
        list.push(f);
        map.set(f.parentKey, list);
      }
    }
    return map;
  }, [allFields]);

  const meta = useMemo(() => metadata ?? {}, [metadata]);

  const handleChange = useCallback(
    (fieldKey: string, newValue: unknown) => {
      onSave({ metadata: { ...meta, [fieldKey]: newValue } });
    },
    [meta, onSave]
  );

  // When a parent field changes, clear all child fields that no longer have valid options.
  const handleParentChange = useCallback(
    (fieldKey: string, newValue: unknown, children: Attribute[]) => {
      onSave({ metadata: metadataAfterChange(meta, fieldKey, newValue, children) });
    },
    [meta, onSave]
  );

  if (topLevelFields.length === 0 && (tab !== undefined || allFields.length === 0)) return null;

  return (
    <div id="item-section-custom-fields" className="card">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Custom Fields</h2>
          <p className="text-sm text-[var(--text-ter)] mt-0.5">Workspace-defined metadata fields.</p>
        </div>
        {mayManage && (
          <Link
            to="/prioritization/settings/custom-fields"
            className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
            title="Manage custom fields"
          >
            <Settings2 className="w-3.5 h-3.5" />
            <span>Manage</span>
          </Link>
        )}
      </div>

      <div className="space-y-5">
        {topLevelFields.map((field) => {
          const children = childFieldsByParent.get(field.key) ?? [];
          const parentValue = meta[field.key];

          return (
            <div key={field.key}>
              {/* Parent field */}
              <FieldRow
                field={field}
                value={parentValue}
                meta={meta}
                readOnly={readOnly}
                onChange={(val) =>
                  children.length > 0
                    ? handleParentChange(field.key, val, children)
                    : handleChange(field.key, val)
                }
              />

              {/* Child fields — only shown when parent has a value */}
              {children.length > 0 && parentValue != null && parentValue !== '' && (
                <div className="ml-4 mt-3 pl-4 border-l-2 border-[var(--slate)] space-y-4">
                  {children.map((child) => {
                    const opts = resolveOptions(child, meta);
                    if (opts.length === 0 && !readOnly) return null; // hide if no options defined for this parent val
                    return (
                      <FieldRow
                        key={child.key}
                        field={child}
                        value={meta[child.key]}
                        meta={meta}
                        readOnly={readOnly}
                        onChange={(val) => handleChange(child.key, val)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FieldRow({
  field,
  value,
  meta,
  readOnly,
  onChange,
}: {
  field: Attribute;
  value: unknown;
  meta: Record<string, unknown>;
  readOnly: boolean;
  onChange: (val: unknown) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-[var(--text-sec)]">
          {field.label}
          {field.required && <span className="text-[var(--coral)] ml-0.5">*</span>}
        </label>
        {field.description && (
          <span className="text-xs text-[var(--text-ter)]">{field.description}</span>
        )}
      </div>
      <FieldInput
        field={field}
        value={value}
        meta={meta}
        readOnly={readOnly}
        onChange={onChange}
      />
    </div>
  );
}
